import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { LuxDialog } from "../common/LuxDialog";
import { ENQUIRY } from "../../config/site";
import { api, apiError, hasBackend } from "../../lib/api";
import { useUI } from "../../context/UIContext";

const EMPTY = { full_name: "", company: "", email: "", phone: "+91 ", city: "", business_type: "", enquiry_type: "General enquiry",
  monthly_quantity: "", bottle_format: "", message: "", consent: false, website: "" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(f) {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (f.company.trim().length < 2) e.company = "Please enter your company, hotel or restaurant.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid work email.";
  const digits = f.phone.replace(/\D/g, "");
  if (!f.phone.trim().startsWith("+") || digits.length < 8) e.phone = "Include the country code, e.g. +91 98765 43210.";
  if (f.city.trim().length < 2) e.city = "Please enter your city.";
  if (!f.business_type) e.business_type = "Please select a business type.";
  if (!f.enquiry_type) e.enquiry_type = "Please select an enquiry type.";
  if (!f.monthly_quantity) e.monthly_quantity = "Please select an approximate requirement.";
  if (!f.bottle_format) e.bottle_format = "Please select a bottle format.";
  if (!f.consent) e.consent = "Please confirm you agree to the privacy policy.";
  return e;
}

const Field = ({ id, label, error, children, className = "" }) => (
  <div className={className}>
    <label htmlFor={id} className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#8C6A3C]">{label}</label>
    {children}
    {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-[#A2412F]" data-testid={`enquiry-error-${id}`}>{error}</p>}
  </div>
);

export const EnquiryModal = () => {
  const { enquiry, closeEnquiry } = useUI();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (enquiry.open) {
      setForm((f) => ({ ...f, enquiry_type: enquiry.type, business_type: enquiry.businessType || f.business_type }));
      setState((s) => (s === "success" ? "idle" : s));
      setServerError("");
    }
  }, [enquiry.open, enquiry.type, enquiry.businessType]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };
  const blur = (k) => () => { const e = validate(form); if (e[k]) setErrors((er) => ({ ...er, [k]: e[k] })); };
  const a = (k) => ({ id: k, value: form[k], onChange: set(k), onBlur: blur(k), "aria-invalid": Boolean(errors[k]), "aria-describedby": errors[k] ? `${k}-error` : undefined, "data-testid": `enquiry-input-${k}` });

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) { document.getElementById(Object.keys(e)[0])?.focus(); return; }
    if (!hasBackend) { setServerError("Enquiry service is not configured on this deployment."); return; }
    setState("loading"); setServerError("");
    try {
      await api.post("/enquiries", { ...form, phone: form.phone.trim() });
      setState("success"); setForm(EMPTY); setErrors({});
    } catch (err) {
      setState("idle"); setServerError(apiError(err));
    }
  };

  const sel = (k, opts, ph) => (
    <select {...a(k)} className="field-lux">
      <option value="" disabled>{ph}</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <LuxDialog open={enquiry.open} onClose={closeEnquiry} eyebrow="Partner with TOYAM" title={state === "success" ? "Enquiry received" : "Begin a conversation"} wide testid="enquiry-dialog">
      {state === "success" ? (
        <div className="py-10 text-center" data-testid="enquiry-success">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-[#B18A4A] text-[#B18A4A]"><Check className="h-7 w-7" strokeWidth={1.2} /></span>
          <p className="font-serif-lux mx-auto mt-8 max-w-xl text-2xl leading-snug">Thank you for your interest in TOYAM. Your enquiry has been received. Our team will review your requirements and get in touch.</p>
          <button type="button" onClick={closeEnquiry} className="btn-line-dark mt-10" data-testid="enquiry-success-close"><span>Close</span></button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="grid gap-x-8 gap-y-7 sm:grid-cols-2" data-testid="enquiry-form">
          <Field id="full_name" label="Full name *" error={errors.full_name}><input {...a("full_name")} autoComplete="name" className="field-lux" /></Field>
          <Field id="company" label="Company / hotel / restaurant *" error={errors.company}><input {...a("company")} autoComplete="organization" className="field-lux" /></Field>
          <Field id="email" label="Work email *" error={errors.email}><input {...a("email")} type="email" autoComplete="email" className="field-lux" /></Field>
          <Field id="phone" label="Phone (with country code) *" error={errors.phone}><input {...a("phone")} type="tel" autoComplete="tel" className="field-lux" /></Field>
          <Field id="city" label="City *" error={errors.city}><input {...a("city")} autoComplete="address-level2" className="field-lux" /></Field>
          <Field id="business_type" label="Business type *" error={errors.business_type}>{sel("business_type", ENQUIRY.businessTypes, "Select business type")}</Field>
          <Field id="enquiry_type" label="Enquiry type *" error={errors.enquiry_type}>{sel("enquiry_type", ENQUIRY.enquiryTypes, "Select enquiry type")}</Field>
          <Field id="monthly_quantity" label="Approx. requirement / month *" error={errors.monthly_quantity}>{sel("monthly_quantity", ENQUIRY.quantities, "Select a range")}</Field>
          <Field id="bottle_format" label="Preferred bottle format *" error={errors.bottle_format} className="sm:col-span-2">{sel("bottle_format", ENQUIRY.formats, "Select a format")}</Field>
          <Field id="message" label="Additional message" className="sm:col-span-2"><textarea {...a("message")} rows={3} maxLength={3000} className="field-lux resize-none" /></Field>
          <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input id="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
          </div>
          <div className="sm:col-span-2">
            <label className="flex cursor-pointer items-start gap-3 text-sm text-[#5f584f]">
              <input type="checkbox" id="consent" checked={form.consent} onChange={set("consent")} aria-invalid={Boolean(errors.consent)} data-testid="enquiry-input-consent"
                className="mt-1 h-4 w-4 shrink-0 accent-[#B18A4A]" />
              <span>I agree that TOYAM may use these details to respond to my enquiry, as described in the <Link to="/privacy" target="_blank" className="underline decoration-[#B18A4A] underline-offset-4" data-testid="enquiry-privacy-link">Privacy Policy</Link>.</span>
            </label>
            {errors.consent && <p role="alert" className="mt-1.5 text-xs text-[#A2412F]" data-testid="enquiry-error-consent">{errors.consent}</p>}
          </div>
          {serverError && <p role="alert" className="border border-[#A2412F]/40 bg-[#A2412F]/5 px-4 py-3 text-sm text-[#A2412F] sm:col-span-2" data-testid="enquiry-server-error">{serverError}</p>}
          <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-[#746D64]">* Required. We'll never share your details.</p>
            <button type="submit" disabled={state === "loading"} className="btn-gold disabled:opacity-70" data-testid="enquiry-submit-btn">
              {state === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /><span>Sending</span></> : <span>Send enquiry</span>}
            </button>
          </div>
        </form>
      )}
    </LuxDialog>
  );
};
